import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { IMG, VALUES } from "../data/content";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="[ À propos ]"
        titleLines={["Le partenaire", "de vos ateliers."]}
        intro="Nova Lean Consulting est né d'une conviction simple : la performance industrielle se construit sur le terrain, avec les équipes, à coups de gestes maîtrisés et de données fiables."
      />

      <section className="bg-paper py-24 md:py-32" data-testid="about-visionmission">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative overflow-hidden rounded-sm">
                <img src={IMG.engineersBlueprint} alt="Ingénieurs sur le terrain" className="w-full h-[520px] object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center gap-12">
            <Reveal>
              <span className="label-mono text-gold">01 — Vision</span>
              <p className="font-display text-2xl md:text-4xl font-medium leading-[1.1] mt-4 tracking-tight">
                Rendre chaque usine plus performante, plus fluide et plus humaine grâce à l'alliance du Lean et du digital.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <span className="label-mono text-gold">02 — Mission</span>
              <p className="text-ink/60 text-lg mt-4 max-w-2xl">
                Accompagner les entreprises industrielles dans leur transformation vers l'excellence opérationnelle : diagnostiquer, déployer, former, et surtout ancrer durablement les résultats au sein des équipes.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink-900 text-paper grain relative py-24 md:py-32" data-testid="about-values">
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <span className="label-mono text-gold">03 — Nos valeurs</span>
            <h2 className="font-display text-4xl md:text-6xl font-semibold mt-4 tracking-tight">Ce qui nous guide.</h2>
          </Reveal>
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-14 border hairline-light rounded-sm overflow-hidden">
            {VALUES.map((v) => (
              <StaggerItem key={v.k} className="bg-ink-900 p-8 min-h-[220px] flex flex-col justify-between hover:bg-ink-800 transition-colors duration-500">
                <span className="font-display text-2xl font-semibold text-gold">{v.k}</span>
                <p className="text-paper/60 text-sm leading-relaxed mt-6">{v.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-paper py-24 md:py-32" data-testid="about-expertise">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <span className="label-mono text-gold">04 — Expertise</span>
              <h2 className="font-display text-3xl md:text-5xl font-medium mt-4 leading-[1.05] tracking-tight">
                Amélioration continue & transformation digitale.
              </h2>
              <p className="text-ink/60 text-lg mt-6 max-w-xl">
                Nos consultants cumulent des années d'expérience sur les lignes de production de secteurs exigeants : automobile, métallurgie, agroalimentaire, aéronautique. Nous parlons le langage de vos opérateurs comme celui de votre direction.
              </p>
            </Reveal>
            <Stagger className="grid grid-cols-2 gap-8 mt-12">
              {[["15+", "Ans d'expérience terrain"], ["8", "Secteurs industriels"], ["240+", "Projets menés"], ["30+", "Formations au catalogue"]].map(([n, l]) => (
                <StaggerItem key={l}>
                  <div className="font-display text-4xl md:text-5xl font-semibold text-ink">{n}</div>
                  <p className="label-mono text-ink/50 mt-2">{l}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal delay={0.1}>
              <img src={IMG.blueprintTable} alt="Analyse de plans industriels" className="w-full h-[560px] object-cover rounded-sm" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink-950 text-paper grain relative py-24 md:py-32 overflow-hidden" data-testid="about-creator">
        <div className="absolute -right-24 top-0 w-[500px] h-[500px] rounded-full bg-gold/8 blur-[130px]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <span className="label-mono text-gold">05 — Le créateur</span>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mt-10 items-start">
            <Reveal className="lg:col-span-4">
              <div className="relative max-w-[340px]">
                <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-sm bg-gold/25" />
                <img src="/images/majdi-zarougui.jpg" alt="Majdi Zarougui — Expert en industrialisation et excellence opérationnelle"
                  className="relative w-full aspect-[4/5] object-cover object-top rounded-sm border border-paper/15 shadow-2xl shadow-ink-950/60" data-testid="creator-photo" />
                <div className="relative mt-5 border-t border-paper/15 pt-4">
                  <div className="font-display text-2xl font-semibold">Majdi Zarougui</div>
                  <div className="label-mono text-steel mt-2">Expert en Industrialisation, Méthodes & Excellence Opérationnelle</div>
                </div>
              </div>
            </Reveal>
            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <h2 className="font-display text-3xl md:text-5xl font-medium leading-[1.08] tracking-tight">
                  L'excellence opérationnelle repose sur la <span className="text-gold-grad">transmission terrain</span>, pas seulement sur les outils.
                </h2>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="text-paper/70 text-lg leading-relaxed mt-8 space-y-5 max-w-3xl">
                  <p>Ingénieur spécialisé en industrialisation, méthodes et amélioration continue, j'ai construit mon expérience au cœur d'environnements industriels exigeants, notamment dans les secteurs <span className="text-paper">aéronautique, ferroviaire, naval et industriel</span>.</p>
                  <p>Mon expertise repose sur une approche très terrain : optimisation des processus, Lean Manufacturing, résolution de problèmes, amélioration de la performance, standardisation des méthodes et accompagnement des équipes opérationnelles.</p>
                  <p>Certifié <span className="text-paper">Lean Six Sigma Green Belt</span> et <span className="text-paper">auditeur interne ISO 9001 / EN 9100</span>, je maîtrise notamment les méthodologies suivantes :</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-5" data-testid="creator-methods">
                  {["DMAIC", "5S", "VSM", "TWI", "AMDEC", "8D", "QRQC", "Yamazumi"].map((m) => (
                    <span key={m} className="label-mono px-3 py-1.5 rounded-full border border-paper/20 text-paper/75">{m}</span>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/majdi-zarougui" data-testid="creator-profile-link"
                    className="group inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-3 label-mono hover:bg-paper transition-colors duration-300">
                    Découvrir le profil complet <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
                  </Link>
                </div>
                <div className="text-paper/70 text-lg leading-relaxed mt-8 space-y-5 max-w-3xl">
                  <p>Au fil de mes expériences, j'ai développé une conviction forte : l'excellence opérationnelle ne repose pas uniquement sur les outils, mais sur la capacité à transmettre les bonnes pratiques, développer les compétences et rendre les méthodes simples et accessibles aux équipes terrain.</p>
                  <p>C'est dans cette logique que je développe aujourd'hui, à travers <span className="text-paper">NovaLean</span>, des outils digitaux, applications, supports de formation et contenus techniques destinés aux professionnels de l'industrie.</p>
                  <p>Mon objectif : associer expérience terrain, Lean Manufacturing, digitalisation et intelligence artificielle pour proposer des solutions concrètes permettant de former plus efficacement, standardiser les processus et améliorer durablement la performance industrielle.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
